import { bucketArray } from "overline";
import { dist } from "vector";
import type { TablesGenerated } from "./database.js";
import type { ItemRecordCore } from "./entities/item.js";
import type { GroupRecordCore as Group } from "./entities/group.js";
import { edgeOffsetPoint } from "@local/extensions/geometry/edge-offset-center.js";

interface Job {
  location_coordinates: {
    longitude: number;
    latitude: number;
  } | null;
  [key: string]: any;
}

interface PricingGroupProperties {
  area: number,
  weight: number,
  quantity: number,
}

interface JobProperties {
  _ungrouped: PricingGroupProperties,
  [key: string]: PricingGroupProperties,
}

interface Supplier {
  address_coordinates: {
    longitude: number;
    latitude: number;
  } | null;
  [key: string]: any;
}

type Type = TablesGenerated<"types">;
type Item = TablesGenerated<"items">;
type ProductList = TablesGenerated<"product_lists">;
type PriceEntry = TablesGenerated<"price_entries">;
type QuoteItem = TablesGenerated<"quote_items">;
type Product = TablesGenerated<"product_entries">;

interface ProductPrices {
  [key: string]: Array<PriceEntry>;
}

interface PriceLibrary {
  [key: string]: ProductPrices;
}

function pricingGroupProps(group: Group) {
  const jp: JobProperties = {
    _ungrouped: {
      area: 0,
      weight: 0,
      quantity: 0,
    },
  };

  if (!group) return jp;

  group.items.order.forEach((id) => {
    const item = group.items[id];
    if (!item.is_collection) {
      const type = group.types[item.type_id];
      if (!type || !type?.pricing_group) {
        jp._ungrouped.area += item.cache?.area || 0;
        jp._ungrouped.weight += item.cache?.weight || 0;
        jp._ungrouped.quantity += item.quantity || 0;
      } else if (type?.pricing_group) {
        if (!jp[type.pricing_group]) {
          jp[type.pricing_group] = {
            area: 0,
            weight: 0,
            quantity: 0,
          };
        }
        jp[type.pricing_group].area += item.cache?.area || 0;
        jp[type.pricing_group].weight += item.cache?.weight || 0;
        jp[type.pricing_group].quantity += item.quantity || 0;
      }
    }
  });

  return jp;
}

function orderProductLists(productLists: Array<ProductList>) {
  const assignments = bucketArray(productLists, "parent_id");
  const unparented = productLists.filter((pl) => !pl.parent_id);
  return unparented.reduce((ordered, pl) => {
    ordered.push(pl);

    if (assignments[pl.id]) {
      ordered.push(...assignments[pl.id]);
    }

    return ordered;
  }, []);
}

function makePriceLibrary(priceEntries: Array<PriceEntry>) {
  const library: PriceLibrary = {};

  return priceEntries.reduce((acc, e) => {
    if (!acc[e.product_id]) {
      acc[e.product_id] = {};
    }

    if (!acc[e.product_id][e.list_id]) {
      acc[e.product_id][e.list_id] = [];
    }

    acc[e.product_id][e.list_id].push(e);

    return acc;
  }, library);
}

function itemArea(item: ItemRecordCore) {
  return item.width && item.height ? (item.cache.width / 12) * (item.cache.height / 12) : 0;
}

function findConstrainedEntry(entries: Array<PriceEntry>, item: ItemRecordCore, jp: PricingGroupProperties) {
  return entries?.find((e) => {
    if (e.minimum_area != null && jp) {
      if (jp.area < e.minimum_area) return false;
    }

    if (e.maximum_area != null && jp) {
      if (jp.area >= e.maximum_area) return false;
    }

    if (e.minimum_item_quantity != null) {
      if (item.quantity < e.minimum_item_quantity) return false;
    }

    if (e.maximum_item_quantity != null) {
      if (item.quantity >= e.maximum_item_quantity) return false;
    }

    if (e.minimum_item_area != null) {
      if (itemArea(item) < e.minimum_item_area) return false;
    }

    if (e.maximum_item_area != null) {
      if (itemArea(item) >= e.maximum_item_area) return false;
    }

    return true;
  });
}

function itemPriceEntries(
  item: ItemRecordCore,
  type: Type, priceLibrary: PriceLibrary,
  jobProperties: JobProperties,
  listId: string) {
  const jp = type?.pricing_group ? jobProperties[type.pricing_group] : jobProperties._ungrouped;

  const itemProdEntries = priceLibrary[type?.product_id]?.[listId];

  if (itemProdEntries?.some((e) => e.unit_price != null)) {
    const entry = findConstrainedEntry(itemProdEntries, item, jp);
    return entry ? [entry] : [];
  }

  if (type?.product_spec?.makeup) {
    const makeupEntries = priceLibrary[type.product_spec.makeup.value]?.[listId];
    if (makeupEntries?.some((e) => e.unit_price != null)) {
      const entry = findConstrainedEntry(makeupEntries, item, jp);
      return entry ? [entry] : [];
    }
  }

  const entries = [] as Array<PriceEntry>;
  if (type.data?.layers) {
    type.data.layers.forEach((layer) => {
      if (layer.material) {
        const prodEntries = priceLibrary[layer.material.id]?.[listId];
        const entry = findConstrainedEntry(prodEntries, item, jp);
        if (entry) entries.push(entry);
      }

      if (layer.inboard_surface) {
        const prodEntries = priceLibrary[layer.inboard_surface.id]?.[listId];
        const entry = findConstrainedEntry(prodEntries, item, jp);
        if (entry) entries.push(entry);
      }

      if (layer.outboard_surface) {
        const prodEntries = priceLibrary[layer.outboard_surface.id]?.[listId];
        const entry = findConstrainedEntry(prodEntries, item, jp);
        if (entry) entries.push(entry);
      }
    });
  }

  return entries;
}

interface Fabrication {
  type: string;
  fab_id?: string;
  product_id?: string;
}

function getFabPriceEntry(fab: Fabrication, priceEntry: PriceEntry, priceLibrary: PriceLibrary, item: ItemRecordCore, jp: PricingGroupProperties) {
  if (!fab) return null;

  // This uses hard-coded ids for the system fabrications. Currently ALL edge fabrications
  // are priced according to the "Generic Edge Fabrication" price entry, and ALL corner
  // fabrications are priced according to the "Generic Corner Fabrication" price entry.

  let fabId = {
    "circular-hole": "4b782348-f2d6-5e5c-891c-eac7607ed815",
    "rectangular-hole": "77facaa3-1420-56cf-b83a-93c87ea024f5",
    "edge-fabrication": "a3690b99-0157-58de-b3ae-8742251079f9",
    "corner-fabrication": "cb40dd9d-dbc3-58d2-b5ee-a7b60c1e052c",
  }[fab.type];

  if (!fabId) {
    fabId = fab.product_id;
  }

  if (!fabId) return null;

  if (priceEntry.fabrication_price_entries) {
    const entry = priceEntry.fabrication_price_entries.find((e) => e.fabrication_id === fabId);
    if (entry) return entry;
  }

  const entries = priceLibrary[fabId]?.[priceEntry.list_id];
  if (entries) {
    const entry = findConstrainedEntry(entries, item, jp);
    if (entry) return entry;
  }

  return null;
}

function edgeTreatmentFabEntry(id: string, productList: ProductList, priceEntry: PriceEntry | undefined, priceLibrary: PriceLibrary) {
  if (!id) return null;
  if (priceEntry?.edge_treatment_price_entries) {
    const entry = priceEntry.edge_treatment_price_entries.find((e) => e.edge_treatment_id === id);
    if (entry) return entry;
  }

  return priceLibrary[id]?.[productList?.id] ?? null;
}

function getPreFabricationPrice(item: ItemRecordCore, entry: PriceEntry | undefined | null) {
  if (!entry) return 0;
  if (!entry.unit_price || !entry.unit) return 0;

  let price = 0;
  if (entry.unit === "item") {
    price = entry.unit_price;
  } else if (entry.unit === "sqft") {
    price = entry.unit_price * itemArea(item);
  } else if (entry.unit === "sqin") {
    price = entry.unit_price * itemArea(item) * 144;
  } else if (entry.unit === "m2") {
    price = entry.unit_price * itemArea(item) * 0.092903;
  }

  return price;
}

function linearPrice(entry: PriceEntry, lenInches: number) {
  if (entry.unit === "in") {
    return entry.unit_price * lenInches;
  } else if (entry.unit === "ft") {
    return entry.unit_price * (lenInches * 0.08333333333);
  } else if (entry.unit === "m") {
    return entry.unit_price * (lenInches * 0.0254);
  } else if (entry.unit === "mm") {
    return entry.unit_price * (lenInches * 25.4);
  } else if (entry.unit === "cm") {
    return entry.unit_price * (lenInches * 2.54);
  }
  return 0;
}

function fabricationPrice(item: ItemRecordCore, fab: Fabrication, entry: PriceEntry) {
  if (!entry?.unit_price || !entry?.unit) return 0;

  if (entry.unit === "each") {
    return entry.unit_price;
  }

  if (fab.type === "two-point") {
    const e1 = item.cache.edges[fab.reference1.loop][fab.reference1.edge];
    const a = edgeOffsetPoint(e1, fab.reference1);
    const e2 = item.cache.edges[fab.reference2.loop][fab.reference2.edge];
    const b = edgeOffsetPoint(e2, fab.reference2);
    const d = dist(a.center, b.center);
    if (d) return linearPrice(entry, d);
  }

  if (fab.type === "edge-aligned") {
    const d = item.cache.edge_lengths[fab.reference.edge];
    if (d) return linearPrice(entry, d);
  }

  return 0;
}

// Fabrications (drilled holes, standoffs, edge/corner work, etc.) are physical
// features of the item itself, so they must be priced ONCE per item — not once
// per resolved price entry. Multi-material items (e.g. a magnetic markerboard,
// whose glass and backing each resolve to a separate price entry) previously
// had every fabrication charge re-added for each entry, inflating the line
// total by the fabrication cost for every extra layer. See EN-284 / EN-287.
function getFabricationPrice(
  item: ItemRecordCore,
  entry: PriceEntry | undefined | null,
  priceLibrary: PriceLibrary,
  jp: PricingGroupProperties,
) {
  if (!item.data.fabrications) return 0;

  let fabPrice = 0;

  item.data.fabrications.voids?.forEach((v) => {
    const e = getFabPriceEntry(v, entry, priceLibrary, item, jp);
    fabPrice += fabricationPrice(item, v, e);
  });

  item.data.fabrications.edge?.forEach((v) => {
    const e = getFabPriceEntry(v, entry, priceLibrary, item, jp);
    fabPrice += fabricationPrice(item, v, e);
  });

  item.data.fabrications.corner?.forEach((v) => {
    const e = getFabPriceEntry(v, entry, priceLibrary, item, jp);
    fabPrice += fabricationPrice(item, v, e);
  });

  item.data.fabrications.virtual?.forEach((v) => {
    const e = getFabPriceEntry(v, entry, priceLibrary, item, jp);
    fabPrice += fabricationPrice(item, v, e);
  });

  item.data.fabrications.additive?.forEach((v) => {
    const e = getFabPriceEntry(v, entry, priceLibrary, item, jp);
    fabPrice += fabricationPrice(item, v, e);
  });

  return fabPrice;
}

/*
This function calculates the price of edge treatments for a given item.

1. Create a map of edge treatment prices, merging generic edge treatment prices,
   makeup-specific edge treatment prices, and item-specific edge treatment prices.
2. Loop through every layer of the item. If the layer is a glass or generic layer,
   check whether the layer has any defined edge treatments. If so, use them; if not,
   fall back to the edge treatment price map.

*/

function edgeTreatmentPrice(
  item: ItemRecordCore,
  type: Type,
  productList: ProductList,
  priceLibrary: PriceLibrary,
  jobProperties: JobProperties,
  entries: Array<PriceEntry>) {
  if (!item || !type) return 0;
  if (!item.data.fabrications?.edge_treatment) return 0;
  if (!type?.data.layers) return 0;

  const jp = type?.pricing_group ? jobProperties[type.pricing_group] : jobProperties._ungrouped;

  const ownItemEntries = priceLibrary[type.product_id]?.[productList.id];
  const ownItemEntry = findConstrainedEntry(ownItemEntries, item, jp);

  const pe = ownItemEntry?.edge_treatment_price_entries?.reduce((acc, e) => {
    acc[e.edge_treatment_id] = e;
    return acc;
  }, {}) || {};

  const treatments = item.data.fabrications.edge_treatment.reduce((acc, id) => {
    if (!acc[id]) {
      const entries = priceLibrary[id]?.[productList.id];
      const entry = findConstrainedEntry(entries, item, jp);
      if (entry) {
        acc[id] = entry;
      }
    }

    return acc;
  }, pe);

  let price = 0;

  const productPrices = entries.reduce((acc, e) => {
    acc[e.product_id] = e.edge_treatment_price_entries.reduce((p, et) => {
      p[et.edge_treatment_id] = et;
      return p;
    }, {});
    return acc;
  }, {});

  item.data.fabrications.edge_treatment.forEach((id, edgeIndex) => {
    type.data.layers.forEach((layer) => {
      if (layer.type !== "glass") return;
      const e = productPrices[layer.material?.id]?.[id] || treatments[id];

      if (e?.unit_price && item.width && item.height) {
        const len = item.cache.edge_lengths[edgeIndex] || 0;
        if (e.unit === "in") {
          price += e.unit_price * len;
        } else if (e.unit === "ft") {
          price += e.unit_price * (len / 12);
        } else if (e.unit === "m") {
          price += e.unit_price * (len / 39.3701);
        } else if (e.unit === "each") {
          price += e.unit_price;
        }
      }
    });

  });

  return price;
}

function getItemPrices(
  group: Group,
  productList: ProductList,
  priceLibrary: PriceLibrary,
  jobProperties: JobProperties) {
  if (!group || !productList || !jobProperties) return {};

  return group.items.order.reduce((acc, itemId) => {
    const item = group.items[itemId];
    if (!item) return acc;

    if (item.price_override != null) {
      acc[item.id] = item.price_override;
      return acc;
    }

    const type = group.types[item.type_id];
    if (!type?.product_id) return acc;

    const entries = itemPriceEntries(item, type, priceLibrary, jobProperties, productList.id);
    const etPrice = edgeTreatmentPrice(item, type, productList, priceLibrary, jobProperties, entries);

    const jp = type?.pricing_group ? jobProperties[type.pricing_group] : jobProperties._ungrouped;

    // Base material price is summed across every resolved price entry (one per
    // layer/material). Fabrications and edge treatment are item-level and are
    // added exactly once. See EN-284 / EN-287.
    const basePrice = entries.reduce((sum, entry) => sum + getPreFabricationPrice(item, entry), 0);
    const fabPrice = entries.length ? getFabricationPrice(item, entries[0], priceLibrary, jp) : 0;
    const priceEach = entries.length ? basePrice + fabPrice + etPrice : null;
    const addPrice = item.added_price || 0;

    acc[item.id] = priceEach + addPrice;

    return acc;
  }, {} as Record<string, number>);
}

function getItemSurchargePrice(
  item: ItemRecordCore,
  entry: PriceEntry,
  itemPrice: number,
) {
  if (!entry) return 0;

  let val = 0;
  if (entry.unit === "item") {
    val = entry.unit_price * item.quantity;
  } else if (entry.unit === "item_price") {
    val = (entry.unit_price / 100) * (itemPrice || 0) * item.quantity;
  } else if (entry.unit === "sqft") {
    val = entry.unit_price * itemArea(item) * item.quantity;
  } else if (entry.unit === "sqin") {
    val = entry.unit_price * itemArea(item) * 144 * item.quantity;
  } else if (entry.unit === "m2") {
    val = entry.unit_price * itemArea(item) * 0.092903 * item.quantity;
  } else {
    val = 0;
  }

  return val;
}

function getSurchargePrices(
  group: Group,
  surcharges: Array<Product>,
  productList: ProductList,
  priceLibrary: PriceLibrary,
  itemPrices: object,
) {
  if (group.surcharges) {
    return group.items.order.reduce((acc, itemId) => {
      const item = group.items[itemId];
      acc[itemId] = {};
      surcharges.forEach((surcharge, i) => {
        acc[itemId][surcharge.id] = item.surcharge_prices?.[i] || 0;
      });
      return acc;
    }, {});
  }

  const surchargedProds = surcharges.map((s) => {
    const prods = s.data?.affected_products.reduce((acc, pid) => {
      acc[pid] = 1;
      return acc;
    }, {}) || {};

    if (s.data?.tiers) {
      s.data.tiers.forEach((tier) => {
        tier.affected_products.forEach((pid) => {
          if (prods[pid] != null) {
            prods[pid] *= tier.multiplier;
          }
        });
      });
    }

    return prods;
  });

  return group.items.order.reduce((acc, itemId) => {
    const item = group.items[itemId];
    const type = group.types[item?.type_id];
    if (!type?.product_id) return acc;

    acc[item.id] = {};

    surcharges.forEach((surcharge, i) => {
      if (item.surcharge_prices?.[i] != null) {
        acc[item.id][surcharge.id] = item.surcharge_prices[i];
        return;
      }

      if (item.surcharge_price_overrides?.[i] != null) {
        acc[item.id][surcharge.id] = item.surcharge_price_overrides[i];
        return;
      }

      const prods = surchargedProds[i];

      if ((!prods[type.product_id])) {
        acc[item.id][surcharge.id] = 0;
        return;
      }

      const entry = priceLibrary[surcharge.id]?.[productList.id]?.[0];

      let price = getItemSurchargePrice(item, entry, itemPrices[item.id]);

      // Apply multipliers
      if (prods[type.product_id] !== 1) {
        price *= prods[type.product_id];
      }

      acc[item.id][surcharge.id] = price;
    });

    return acc;
  }, {});
}

function qiPrice(quoteItem: QuoteItem) {
  if (quoteItem.price_override != null) {
    return quoteItem.price_override;
  }

  return quoteItem.total_price;
}

function priceSubtotal(
  group: Group,
  items: Array<ItemRecordCore>,
  productList: ProductList,
  priceLibrary: PriceLibrary,
  jobProperties: JobProperties,
  surcharges: Array<Product>,
) {
  const itemPriceObj = getItemPrices(group, productList, priceLibrary, jobProperties);
  const surchargePrices = getSurchargePrices(group, surcharges, productList, priceLibrary, itemPriceObj);
  const itemPrices = items.reduce((acc, item) => {
    const scprices = surcharges.map((s) => surchargePrices?.[item.id]?.[s.id] || 0);
    const surcharge_total = scprices.reduce((sum, price) => sum + price, 0);
    return acc + (itemPriceObj[item.id] || 0) * item.quantity + surcharge_total;
  }, 0);
  const quoteItemPrices = group.quote_items.reduce((sum, qi) => sum + qiPrice(qi), 0);
  return itemPrices + quoteItemPrices;
}

export type { ProductList, PriceEntry, PriceLibrary };
export {
  pricingGroupProps,
  itemPriceEntries,
  makePriceLibrary,
  orderProductLists,
  getPreFabricationPrice,
  getItemPrices,
  getItemSurchargePrice,
  getSurchargePrices,
  priceSubtotal,
};
